import React, { useState, useEffect } from 'react';
import { parseBankStatementCSV } from "./utils/csv-parser";
import { NavBar } from './components/Header';
import { TabLayout } from "./components/TabLayout";
import { TransactionsView } from "./components/TransactionsView";
import { SummaryView } from "./components/SummaryView";
import { BudgetView } from "./components/BudgetView";
import "./index.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GoogleSheetUtil } from "./utils/GoogleSheetUtil";
import { useToast } from "../../component/common/Toast";

const FullFinance = () => {
  const [sheetIds, setSheetIds] = useState({
    workbookId: null,
    transactionsSheetId: null,
    summarySheetId: null,
    budgetSheetId: null,
  });
  const [transactions, setTransactions] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [budgetEstimates, setBudgetEstimates] = useState([]);
  const [categoryVisibility, setCategoryVisibility] = useState({});
  const [accountVisibility, setAccountVisibility] = useState({});
  const [googleAuthToken, setGoogleAuthToken] = useState(null);
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const [writing, setWriting] = useState(false);
  const { showToast } = useToast();

  // Load data from session storage on component mount
  useEffect(() => {
    document.title = "Full Finance";

    // Load Google Identity Services script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      console.log("Google Identity Services script loaded");
      setIsGoogleScriptLoaded(true);
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    try {
      const newTransactions = [];

      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        const result = await parseBankStatementCSV(file, fileIndex);
        if (result instanceof Error) {
          console.error("CSV parsing error:", result);
          showToast("CSV parsing error:" + result.message, "error");
        } else {
          newTransactions.push(...result);
        }
      }

      setTransactions((prev) => {
        const updatedTransactions = [...prev, ...newTransactions];
        sessionStorage.setItem(
          "transactions",
          JSON.stringify(updatedTransactions)
        );
        setWriting(true);
        return updatedTransactions;
      });
    } catch (err) {
      console.error("CSV parsing error:", err);
      showToast("CSV parsing error:" + err.message, "error");
      setWriting(false);
    }
  };

  const toggleCategoryVisibility = (category) => {
    setCategoryVisibility((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleAccountVisibility = (account) => {
    setAccountVisibility((prev) => ({
      ...prev,
      [account]: !prev[account],
    }));
  };

  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return (
      <div style={{ padding: "20px" }}>
        <p style={{ color: "#666" }}>
          Google Sheets integration is not configured.
        </p>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
    >
      <div className="finance-container">
        <NavBar onFileUpload={handleFileUpload} />
        <GoogleSheetUtil
          sheetIds={sheetIds}
          setSheetIds={setSheetIds}
          authToken={googleAuthToken}
          setAuthToken={setGoogleAuthToken}
          isGoogleScriptLoaded={isGoogleScriptLoaded}
          transactions={transactions}
          setTransactions={setTransactions}
          summaryData={summaryData}
          setSummaryData={setSummaryData}
          budgetEstimates={budgetEstimates}
          setBudgetEstimates={setBudgetEstimates}
          writing={writing}
          setWriting={setWriting}
        />
        {sheetIds.workbookId ? (
          <div className="finance-content">
            <TabLayout
              tabs={[
                {
                  label: "Transactions",
                  content: (
                    <TransactionsView
                      transactions={transactions}
                      categoryVisibility={categoryVisibility}
                      accountVisibility={accountVisibility}
                      toggleCategoryVisibility={toggleCategoryVisibility}
                      toggleAccountVisibility={toggleAccountVisibility}
                    />
                  ),
                },
                {
                  label: "Summary",
                  content: <SummaryView summaryData={summaryData} />,
                },
                {
                  label: "Budget",
                  content: <BudgetView budgetEstimates={budgetEstimates} />,
                },
              ]}
            />
          </div>
        ) : (
          <p className="empty-message">
            {sheetIds.workbookId
              ? "No summary data available. Upload transactions to create a summary."
              : "Sign in with Google to enable spreadsheet integration."}
          </p>
        )}
      </div>
    </GoogleOAuthProvider>
  );
};

export default FullFinance; 